<template>
  <component
    :is="href ? 'a' : 'button'"
    :type="href ? undefined : buttonType"
    :href="href || undefined"
    class="base-button"
    :class="[
      `button-${type}`,
      { 'custom-hover': hoverBackgroundColor },
      { 'no-underline': href },
      { '!cursor-not-allowed': disabled, [disabledClass]: disabled },
    ]"
    :style="{
      width: buttonWidth,
      height: buttonHeight,
      backgroundColor: bgColor,
      color: textColor,
      borderColor: setBorderColor,
    }"
    :disabled="href ? undefined : loading || disabled"
    :aria-disabled="href && (loading || disabled) ? 'true' : undefined"
  >
    <LazyBaseSpinner v-if="loading" :color="textColor" />
    <template v-else>
      <component
        :is="prependIcon"
        v-if="prependIcon"
        class="prepend-icon"
        :class="iconClass"
        :style="{
          fontSize: iconFontSize,
          color: setIconColor,
        }"
      />

      <component
        :is="icon"
        v-if="type === 'icon'"
        class="icon"
        :style="{
          fontSize: iconFontSize,
          color: setIconColor,
        }"
      />
      <span v-else :class="font ? font : ''">
        {{ label }}
        <slot />
      </span>

      <component
        :is="appendIcon"
        v-if="appendIcon"
        class="append-icon"
        :class="iconClass"
        :style="{
          fontSize: iconFontSize,
          color: setIconColor,
        }"
      />
    </template>
  </component>
</template>

<script setup>
const props = defineProps({
  buttonType: { type: String, default: "button" }, // button, submit,..
  // When set, renders a real <a href> (e.g. crawlable pagination links) instead of a <button>.
  href: { type: String, default: null },
  type: { type: String, default: "primary" }, // BUTTON: primary|secondary|third|icon|outlined|text|chips
  label: { type: String, default: "click" },
  width: { type: Number, default: null },
  height: { type: Number, default: null },
  icon: { type: String, default: null },
  iconSize: { type: Number, default: null },
  iconColor: { type: String, default: null },
  prependIcon: { type: String, default: null },
  appendIcon: { type: String, default: null },
  backgroundColor: { type: String, default: null },
  color: { type: String, default: null },
  font: { type: String, default: null },
  hoverColor: { type: String, default: null },
  hoverIcon: { type: String, default: null },
  hoverBackgroundColor: { type: String, default: null }, // if gou need to change background in hover, change hoverColor too
  iconClass: { type: String, default: null },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  disabledClass: { type: String, default: null },
});

const buttonWidth = computed(() => {
  return props.width ? props.width + "px" : null;
});

const buttonHeight = computed(() => {
  return props.height ? props.height + "px" : null;
});

const textColor = computed(() => {
  return props.color ? `var(--${props.color})` : null;
});

const bgColor = computed(() => {
  return props.backgroundColor ? `var(--${props.backgroundColor})` : null;
});

const iconFontSize = computed(() => {
  return props.iconSize ? props.iconSize + "px" : null;
});

const setIconColor = computed(() => {
  return props.iconColor ? `var(--${props.iconColor})` : null;
});

const setBorderColor = computed(() => {
  return props.type === "icon" && props.iconColor
    ? `var(--${props.iconColor})`
    : (props.type === "outlined" || props.type === "third") && props.color
      ? `var(--${props.color})`
      : null;
});

const hoverColorVar = computed(() => {
  return props.hoverColor ? `var(--${props.hoverColor})` : "inherit";
});
const hoverIconVar = computed(() => {
  return props.hoverIcon ? `var(--${props.hoverIcon})` : "inherit";
});

const hoverBGColorVar = computed(() => {
  return props.hoverBackgroundColor
    ? `var(--${props.hoverBackgroundColor})`
    : "inherit";
});
</script>

<style scoped>
.base-button.custom-hover:hover,
.base-button.custom-hover:focus,
.base-button.custom-hover:active {
  background-color: v-bind(hoverBGColorVar) !important;
  color: v-bind(hoverColorVar) !important;
  svg {
    color: v-bind(hoverIconVar) !important;
  }
}
</style>
